import { patchUrlMappings } from '@discord/embedded-app-sdk';

/**
 * Wire Discord's CSP-enforced proxy for the Colyseus WebSocket.
 *
 * In production (inside the Discord Activity iframe), the iframe is sandboxed
 * at `https://<client_id>.discordsays.com`. All `fetch` / `WebSocket` / `XHR`
 * to external hosts must route through `<client_id>.discordsays.com/.proxy/<path>`,
 * with the mapping defined in the Discord Developer Portal's URL Mappings tab.
 *
 * `patchUrlMappings` (from `@discord/embedded-app-sdk`) monkey-patches
 * `WebSocket`, `fetch`, `XMLHttpRequest.open`, and src-attribute setters so the
 * SPA can keep using the raw target URL (e.g. `wss://susdle-server.fly.dev`)
 * and the library rewrites the request URL at runtime to the `.proxy/` path.
 *
 * Must be called ONCE, BEFORE any code that opens a fetch/WS. In Susdle that
 * means before constructing `RealDiscordSdkAdapter` and before
 * `RealColyseusClient` — both happen inside `buildSessionPromise` in App.tsx.
 *
 * VERIFIED on 2026-05-30 against @discord/embedded-app-sdk@2.5.0
 * (output/utils/patchUrlMappings.d.ts):
 * - patchUrlMappings(mappings, config?) where Mapping = { prefix, target }
 * - All four patch flags default to true; we don't override
 *
 * @param colyseusUrl - the raw `wss://...` URL from `VITE_COLYSEUS_URL`. The
 *   hostname is extracted and used as the mapping target (Discord's URL
 *   Mappings target field accepts hostnames only — no scheme, no path).
 */
export function applyDiscordProxyMappings(colyseusUrl: string): void {
  const url = new URL(colyseusUrl);
  if (url.protocol !== 'wss:' && url.protocol !== 'ws:') {
    throw new Error(
      `applyDiscordProxyMappings: expected wss:// URL, got ${url.protocol}`,
    );
  }
  patchUrlMappings([
    { prefix: '/colyseus', target: url.host },
  ]);
}
